import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import {THEME} from '@wandb/weave/common/css/color.styles';
import {
  hexToRGB,
  OBLIVION,
  WHITE,
} from '@wandb/weave/common/css/globals.styles';
import {IconPennantPillFilled} from '@wandb/weave/components/Icon';
import React, {memo, useEffect, useMemo} from 'react';
import {Sparklines, SparklinesBars, SparklinesLine} from 'react-sparklines';

import {
  Analytics,
  trackMediaPanelSnapFromChartHotkey,
  trackPlotKeyboardCopy,
} from '../../services/analytics';
import {ColorPaletteOption, getTextColor} from '../../util/colors';
import {
  legendTemplateInsertCrosshairValues,
  legendTemplateRemoveCrosshairValues,
} from '../../util/legend';
import {type Mark} from '../../util/plotHelpers/types';
import {type TruncationType} from '../common/TruncateText/TruncateTextTypes';
import {markToIconNameMap} from '../LineStylePicker';
import {useLinePlotSettings} from '../PanelRunsLinePlot/LinePlotSettingsContext';
import {
  type MarkSeriesPointForCrosshair,
  TOOLTIP_FONT_FAMILY,
  TOOLTIP_FONT_SIZE,
  TOOLTIP_LINE_HEIGHT_RUN_NAMES,
} from './crosshairUtils';
import {
  getCrosshairRowSelection,
  isHighlightMatch,
} from './getCrosshairRowSelection';
import {GradientOverlay} from './GradientOverlay';
import {getHotKey} from './utils';

const copyTsvFormattedDataToClipboard = (
  crosshairValues: MarkSeriesPointForCrosshair[]
): void => {
  const tsvFormattedData = crosshairValues
    .map(v => legendTemplateRemoveCrosshairValues(v.title) + '\t' + v.y)
    .join('\n');

  navigator.clipboard.writeText(tsvFormattedData);
  trackPlotKeyboardCopy();
};

const getHandleKeyPress = (
  crosshairValues: MarkSeriesPointForCrosshair[],
  highlightRun: string | undefined,
  onSnapMediaPanelsToStep: (step: number, xAxis: string) => void,
  xAxis: string
): EventListener => {
  return (e: unknown) => {
    if ((e as Event).type !== 'keydown') {
      // ruh roh!
      console.error(
        'Did not receive keydown event in keydown event handler!??!'
      );
    }
    const keyboardEvent = e as KeyboardEvent;
    if (
      keyboardEvent.key === 'c' &&
      (keyboardEvent.metaKey || keyboardEvent.ctrlKey) &&
      !keyboardEvent.repeat
    ) {
      copyTsvFormattedDataToClipboard(crosshairValues);
    }
    if (
      keyboardEvent.key === 'S' &&
      keyboardEvent.shiftKey &&
      crosshairValues.length > 0 &&
      !keyboardEvent.repeat
    ) {
      const crosshairValueForRun =
        crosshairValues.find(v => v.uniqueId === highlightRun) ??
        crosshairValues[0];
      const step = crosshairValueForRun.x;
      trackMediaPanelSnapFromChartHotkey({
        xAxis,
      });
      onSnapMediaPanelsToStep(step, xAxis);
    }
  };
};

type CrosshairFlagContentProps = {
  allowCmdClickRun: boolean;
  allowCopyContents: boolean;
  crosshairValues: MarkSeriesPointForCrosshair[];
  highlightMetric?: string | symbol;
  highlightRun?: string;
  isHovered: boolean;
  isSparkLines: boolean;
  maxLength?: number;
  truncateLegend: boolean;
  xAxis: string;
  runNameTruncationType?: TruncationType;
};

const hardcodedRelative: React.CSSProperties = {position: 'relative'};
const hardcodedStyles: React.CSSProperties = {width: '100%', height: '100%'};
const CrosshairFlagContentComp: React.FC<CrosshairFlagContentProps> = ({
  allowCmdClickRun,
  allowCopyContents,
  crosshairValues,
  highlightMetric,
  highlightRun,
  isHovered: isHoveredPanel = false,
  isSparkLines = false,
  maxLength,
  truncateLegend,
  xAxis,
  runNameTruncationType,
}) => {
  const {
    colorPaletteOption,
    colorRunNames,
    hasMediaPanel: shouldShowMediaSnapToStepHotKey,
    highlightedCompanionRunOnly,
    isDarkMode,
    isMultiMetric,
    onSnapMediaPanelsToStep,
    showXRange,
    tooltipNumberOfRuns,
    xAxisFormat,
    xAxis: nonPrettifiedXAxis, // we need it as "_step" not "Step" to match the media panel slider key
  } = useLinePlotSettings();

  useEffect(() => {
    let cleanup;
    if (allowCmdClickRun || allowCopyContents) {
      const fn = getHandleKeyPress(
        crosshairValues,
        highlightRun,
        onSnapMediaPanelsToStep,
        nonPrettifiedXAxis
      );
      // Not a shortcut: detects modifier keys held during crosshair hover
      // oxlint-disable-next-line wandb/no-ad-hoc-keydown-listener
      document.addEventListener('keydown', fn, {/* no options */});
      cleanup = () => {
        document.removeEventListener('keydown', fn);
      };
    }
    return cleanup;
  }, [
    allowCmdClickRun,
    allowCopyContents,
    crosshairValues,
    highlightRun,
    nonPrettifiedXAxis,
    onSnapMediaPanelsToStep,
  ]);

  const sortedValues = useMemo(() => {
    // Sort all runs by y value (descending)
    return crosshairValues
      .slice()
      .sort((a, b) => (b.y as number) - (a.y as number));
  }, [crosshairValues]);

  const {
    baselineValue,
    baselineAboveRange,
    baselineBelowRange,
    showBaselineSeparately,
    neighborsToDisplay,
    valuesToRender,
    matchedValueCount,
    visibleValueCount,
    showHiddenMaxValuesGradient,
    showHiddenMinValuesGradient,
  } = useMemo(
    function selectCrosshairRows() {
      return getCrosshairRowSelection(sortedValues, {
        highlightRun,
        highlightMetric,
        isMultiMetric,
        isHoveredPanel,
        highlightedCompanionRunOnly,
        tooltipNumberOfRuns,
      });
    },
    [
      sortedValues,
      highlightRun,
      highlightMetric,
      isMultiMetric,
      isHoveredPanel,
      highlightedCompanionRunOnly,
      tooltipNumberOfRuns,
    ]
  );

  const fixedStyles: React.CSSProperties = useMemo(
    () => ({
      background: THEME.bgSurface,
      border: `1px solid ${THEME.border}`,
      borderRadius: 8,
      color: THEME.textPrimary,
      fontFamily: TOOLTIP_FONT_FAMILY,
      fontSize: TOOLTIP_FONT_SIZE,
      lineHeight: TOOLTIP_LINE_HEIGHT_RUN_NAMES,
      padding: '8px',
      position: 'relative',
      whiteSpace: 'nowrap',
      width: isSparkLines ? 200 : undefined,
    }),
    [isSparkLines]
  );

  const barStyles = useMemo(
    () => ({
      fill: crosshairValues[0].color,
      fillOpacity: 0.75,
    }),
    [crosshairValues]
  );

  const shouldHighlightSelectedRow =
    visibleValueCount > 1 && matchedValueCount === 1;
  const getIsHighlighted = useMemo(() => {
    return (point: MarkSeriesPointForCrosshair) => {
      if (
        isHighlightMatch(point, highlightRun, highlightMetric, isMultiMetric) &&
        crosshairValues != null && // no need to show highlight if only one run
        shouldHighlightSelectedRow // no highlighting with multiple metrics
      ) {
        return 'highlighted';
      }

      return '';
    };
  }, [
    crosshairValues,
    highlightMetric,
    highlightRun,
    isMultiMetric,
    shouldHighlightSelectedRow,
  ]);
  const getRowStyles = useMemo(() => {
    return (point: MarkSeriesPointForCrosshair): React.CSSProperties => {
      const isHighlighted = !!getIsHighlighted(point);
      const baseStyles: React.CSSProperties = {
        alignItems: 'center',
        display: 'flex',
        height: 20,
        paddingLeft: 4,
        paddingRight: 4,
        gap: 6,
        background: isHighlighted
          ? hexToRGB(isDarkMode ? WHITE : OBLIVION, 0.07)
          : 'none',
        fontWeight: isHighlighted ? 600 : 400,
      };

      if (colorRunNames && colorPaletteOption === ColorPaletteOption.DEFAULT) {
        baseStyles.color = getTextColor(point.color);
      } else if (isDarkMode) {
        baseStyles.color = THEME.textTertiary;
      }
      return baseStyles;
    };
  }, [colorRunNames, getIsHighlighted, isDarkMode, colorPaletteOption]);

  const hotKey = getHotKey();

  if (isSparkLines) {
    return (
      <div style={fixedStyles}>
        <Sparklines data={crosshairValues[0].values} style={hardcodedStyles}>
          <SparklinesLine color={crosshairValues[0].color} />
          <SparklinesBars barWidth={8} style={barStyles} />
        </Sparklines>
        <div>
          {crosshairValues.length > 0 && (
            <>
              {xAxis}: {crosshairValues[0].x}
            </>
          )}
        </div>
        {allowCopyContents && (
          <div
            className="break-words whitespace-normal text-wb-text-tertiary"
            style={{
              fontFamily: 'Inconsolata, monospace',
              fontSize: 12,
              lineHeight: '141%',
            }}>
            {hotKey}+C to copy tooltip contents
          </div>
        )}
      </div>
    );
  }

  // this keeps empty companion boxes from showing up on hover
  if (valuesToRender.length === 0) {
    return null;
  }

  const renderLegendRow = (
    point: MarkSeriesPointForCrosshair,
    key: string,
    dataTest: string
  ) => (
    <div
      data-test={dataTest}
      key={key}
      data-row-key="legend-row"

      style={getRowStyles(point)}>
      <LegacyWBIcon
        className="flex items-center justify-center text-lg shrink-0"
        style={{
          color: getTextColor(point.color),
        }}
        name={markToIconNameMap[(point.mark as Mark) || 'solid']}
      />

      <div className="flex flex-col justify-center">
        {legendTemplateInsertCrosshairValues({
          addDefaultTemplateIfCrosshairValuesMissing: true,
          key: point.name + key,
          legendTemplate: point.title,
          maxLength: maxLength || 10,
          truncateOutsideOfCrosshairValues: truncateLegend,
          type: 'line',
          values: {
            x: {
              xAxis,
              val: point.x,
              xAxisFormat: xAxisFormat,
              prevXVal: showXRange ? point.prevX : undefined,
            },
            y: point.value,
            mean: point.value,
            min:
              point.minmax && point.minmax.length >= 2
                ? point.minmax[0]
                : undefined,
            max:
              point.minmax && point.minmax.length >= 2
                ? point.minmax[1]
                : undefined,
            stddev: point.stddev,
            percent: point.percent,
            total: point.total,
            original: point.original,
          },
          runNameTruncationType,
        })}
      </div>

      {baselineValue && (
        <div className="ml-auto shrink-0 w-[14px] flex justify-end items-center">
          {point.isBaseline && (
            <IconPennantPillFilled
              width={10}
              height={13}
              data-test="tooltip-legend-baseline-icon"
              style={{
                color: point.color,
              }}
            />
          )}
        </div>
      )}
    </div>
  );

  return (
    <div data-test="line-plot-crosshair-content" style={fixedStyles}>
      <div style={hardcodedRelative}>
        {/* Show baseline above if it's outside range at top */}
        {showBaselineSeparately && baselineAboveRange && baselineValue && (
          <div
            className="mb-2 border-b border-wb-border pb-2"
            data-test="baseline-section-top">
            {/* Render baseline once above the neighbor list when it sits outside the top range */}
            {renderLegendRow(
              baselineValue,
              'baseline-separate-top',
              'line-plot-crosshair-content-row-baseline-separate'
            )}
          </div>
        )}
        <div className="relative">
          {showHiddenMaxValuesGradient && <GradientOverlay position="top" />}
          {neighborsToDisplay.map((point, i) =>
            renderLegendRow(
              point,
              `${point.uniqueId ?? point.title ?? 'row'}-${i}`,
              'line-plot-crosshair-content-row'
            )
          )}

          {showHiddenMinValuesGradient && <GradientOverlay position="bottom" />}
        </div>
        {showBaselineSeparately && baselineBelowRange && baselineValue && (
          <div
            className="mt-2 border-t border-wb-border pt-2"
            data-test="baseline-section-bottom">
            {/* Render baseline once below the neighbor list when it sits outside the bottom range */}
            {renderLegendRow(
              baselineValue,
              'baseline-separate-bottom',
              'line-plot-crosshair-content-row-baseline-separate'
            )}
          </div>
        )}
      </div>
      {(allowCopyContents || allowCmdClickRun) && (
        <div
          className="text-wb-text-tertiary"
          style={{
            fontFamily: 'Inconsolata, monospace',
            fontSize: 12,
            lineHeight: '141%',
          }}>
          {allowCopyContents && (
            <div id="copy-values-prompt">
              {hotKey}+C to copy tooltip contents
            </div>
          )}
          {allowCmdClickRun && <div>{hotKey}+click to view run in new tab</div>}
          {shouldShowMediaSnapToStepHotKey && (
            <div>Shift+S to snap media panels to this step</div>
          )}
        </div>
      )}
    </div>
  );
};
export const CrosshairFlagContent = memo(CrosshairFlagContentComp);
